import React from 'react'
import { cn } from 'components/UI/cn'

export type StatusBadgeColor =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'

interface StatusBadgeProps {
  color?: StatusBadgeColor
  children: React.ReactNode
  /** Optional leading icon. Sized to match the badge text. */
  icon?: React.ComponentType<{ className?: string }> | null
  className?: string
}

// Every colored variant pairs the `-bg-subtle` fill with the `-strong` text
// token. That pairing is the only combination that clears WCAG AA for all
// colors — the base tokens on their saturated `-bg` fills all fail. Using this
// component instead of hand-writing `bg-X-bg text-X` is what keeps new badges
// accessible by default.
const CLASSES_BY_COLOR: Record<StatusBadgeColor, string> = {
  success: 'bg-success-bg-subtle text-success-strong',
  warning: 'bg-warning-bg-subtle text-warning-strong',
  error: 'bg-error-bg-subtle text-error-strong',
  info: 'bg-info-bg-subtle text-info-strong',
  neutral: 'bg-ds2026-badge-gray-bg text-ds2026-badge-gray-text',
}

export function StatusBadge({
  color = 'neutral',
  children,
  icon,
  className,
}: StatusBadgeProps) {
  const Icon = icon
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        CLASSES_BY_COLOR[color],
        className
      )}
    >
      {Icon && <Icon className="size-3.5 shrink-0" />}
      {children}
    </span>
  )
}
